import React, { type FunctionComponent } from 'react'
import {
  AccountIcon,
  CategoryIcon,
  ComputerIcon,
  EditIcon,
  FulfillmentTestingCenterIcon,
  GraphIcon,
  HelpIcon,
  HomeIcon,
  InsightIcon,
  InventoryGoodIcon,
  ItemIcon,
  LegalIcon,
  ListIcon,
  OrderIcon,
  RatingIcon,
  StoreIcon,
  TechnicalIcon,
  TrendingIcon,
  UserIcon,
} from '@instacart/ids-core'
import { TopProductsAccessControl } from 'legacy/routes/reports/analytics/top-products/TopProducts.configuration'
import { NavItem } from 'gin-and-tonic/containers/nav/components/NavItem'
import { NavFolder } from 'gin-and-tonic/containers/nav/components/NavFolder'
import ko from 'legacy/common/knowledgeOwl'
import { CatalogSectionAccessControl } from 'legacy/sections/catalog/CatalogSection.configuration'
import {
  OrdersDeliveryAccessControl,
  OrdersPickupAccessControl,
  OrdersScanAndPayAccessControl,
} from 'legacy/sections/orders/OrdersSection.configuration'
import { GeneratedReportsSectionAccessControl } from 'legacy/sections/generated-reports/GeneratedReportsSection.configuration'
import { InventoryFilesAccessControl } from 'legacy/routes/reports/files/InventoryFiles.configuration'
import { TlogsContainerAccessControl } from 'legacy/routes/reports/tlogs/TlogsContainer.configuration'
import { AlcoholTlogsAccessControl } from 'legacy/routes/reports/alcohol/AlcoholTlogs.configuration'
import { RxTlogsAccessControl } from 'legacy/routes/reports/rx/RxTlogs.configuration'
import { ReportsPickupAnalyticsAccessControl } from 'domains/reports/pages/pickup-analytics/ReportsPickupAnalytics.configuration'
import { AdminAccessControl } from 'legacy/routes/admin/Admin.configuration'
import { StripeAssociationsAccessControl } from 'legacy/routes/settings/stripe/StripeAssociations.configuration'
import { StoreGroupsSectionNavigationControl } from 'legacy/sections/store-groups/StoreGroupsSection.configuration'
import { MerchandisingSectionAccessControl } from 'domains/merchandising/MerchandisingSection.configuration'
import { NavItem as NavEntryPoint } from 'legacy/components/Nav/nav.types'
import { ReportsNormalizedReportsListPageAccessControl } from 'domains/reports/pages/normalized-reports-list/ReportsNormalizedReportsListPage.configuration'
import { AppAdminSectionAccessControl } from 'sections/admin/AdminSection.configuration'
import { AccountManagementRetailerAccessControl } from 'domains/account-management/AccountManagementRetailerSection.configuration'
import { ConnectSectionAccessControl } from 'domains/connect/ConnectSection.configuration'
import { usePickupWelcomeTour } from 'domains/pickup-settings/training-tour/pickupWelcomeTourSteps'
import { StoreOperationsRouteAccessControl } from 'gin-and-tonic/containers/nav/utils/storeOperationsRouteAccessControl'
import { usePickupTourStatus } from 'domains/pickup-settings/training-tour/usePickupTourStatus'
import { type AccessControlConfig } from 'legacy/common/accessControl/accessControl.types'
import clientEnv from 'utils/global/clientEnv'
import {
  AppDevPortalSectionAccessControl,
  DevPortalFTCLinkAccessControl,
} from 'sections/dev-portal/DevPortalSection.configuration'
import {
  insightsPortalRedesignAndInfraAccessControl,
  insightsPortalRedesignAndInfraDeprecatedFolderAccessControl,
} from 'domains/insights-portal/access-control/insightsPortalRedesignAndInfraAccess.configuration'
import { retailerFundedMarketingAccessControl } from 'domains/retailer-funded-marketing/access-control/retailerFundedMarketingAccess.configuration'
import { insightsPortalInventoryIntelligenceAccessControl } from 'domains/insights-portal/access-control/insightsPortalInventoryIntelligence.configuration'
import { onboardingAccessControl } from 'domains/onboarding/access-control/onboardingAccess.configuration'
import { useCheckLaunchOnboardingProgress } from 'domains/onboarding/utils/useCheckLaunchOnboardingProgress.hooks'

export const RetailerNavMainContent: FunctionComponent = () => {
  const { isTourPending, isTourCompleted } = usePickupTourStatus('pickup_overview')
  const { startTour: startPickupWelcomeTour } = usePickupWelcomeTour()
  const { hasInProgressLaunchOnboarding, hasLaunchOnboarding } = useCheckLaunchOnboardingProgress()

  return (
    <React.Fragment>
      <NavItem isMainItem Icon={HomeIcon} route="dashboard" title="home.title" />
      {hasInProgressLaunchOnboarding && (
        <NavItem
          isMainItem
          Icon={ListIcon}
          route="onboarding-launch-checklist"
          title="onboarding.launchChecklist"
          accessControl={onboardingAccessControl as AccessControlConfig}
        />
      )}

      <NavFolder
        Icon={ItemIcon}
        title="catalog.title"
        accessControl={CatalogSectionAccessControl}
        navEntryPoint={NavEntryPoint.Catalog}
      >
        <NavItem route="departments" title="catalog.departments.title" />
        <NavItem route="new-product-request" title="catalog.requestNewProduct" />
      </NavFolder>

      <NavFolder Icon={OrderIcon} title="orders.title" navEntryPoint={NavEntryPoint.Orders}>
        <NavItem
          accessControl={OrdersDeliveryAccessControl}
          route="delivery"
          title="delivery.title"
        />
        <NavItem
          accessControl={OrdersPickupAccessControl}
          route="pickup"
          title="pickup.title"
          id="nav-item-pickup"
        />
        <NavItem
          accessControl={OrdersScanAndPayAccessControl}
          route="scanandpay"
          title="scanandpay.title"
          id="nav-item-scanandpay"
        />
      </NavFolder>
      <NavFolder Icon={LegalIcon} title="files.title" navEntryPoint={NavEntryPoint.Files}>
        <NavItem
          route="files"
          title="reports.files.title"
          accessControl={InventoryFilesAccessControl}
        />
        <NavItem
          route="tlogs"
          title="tlogs.title"
          accessControl={TlogsContainerAccessControl}
          id="nav-item-tlogs"
        />
        <NavItem
          route="alcohol-tlogs"
          title="alcoholTlogs.title"
          accessControl={AlcoholTlogsAccessControl}
        />
        <NavItem route="rx-tlogs" title="rxTlogs.title" accessControl={RxTlogsAccessControl} />
      </NavFolder>
      <NavFolder
        Icon={InsightIcon}
        title="insightsPortalDomain.nav.dataAndAnalytics.folder"
        navEntryPoint={NavEntryPoint.InsightsPortal}
        accessControl={
          insightsPortalRedesignAndInfraDeprecatedFolderAccessControl as AccessControlConfig
        }
      >
        <NavItem
          route="pickupAnalytics"
          title="reportsDomain.pickupAnalytics.title"
          id="nav-item-pickupAnalytics"
          accessControl={ReportsPickupAnalyticsAccessControl}
        />
      </NavFolder>
      <NavFolder
        Icon={TrendingIcon}
        title="insightsPortalDomain.nav.dataAndAnalytics.folder"
        navEntryPoint={NavEntryPoint.DataAndAnalytics}
        accessControl={insightsPortalRedesignAndInfraAccessControl as AccessControlConfig}
      >
        <NavItem
          route="pickupAnalytics"
          title="reportsDomain.pickupAnalytics.title"
          id="nav-item-pickupAnalytics"
          accessControl={ReportsPickupAnalyticsAccessControl}
        />
      </NavFolder>
      <NavFolder Icon={GraphIcon} title="reports.title" navEntryPoint={NavEntryPoint.Reports}>
        <NavItem
          route="generated-reports"
          title="generatedReports.title"
          accessControl={GeneratedReportsSectionAccessControl}
        />
        <NavItem
          route="top-products"
          title="topProducts.title"
          accessControl={TopProductsAccessControl}
        />
        <NavItem
          route="reports-normalized-reports-list"
          title="reportsDomain.nav.label"
          id="nav-item-reports"
          accessControl={ReportsNormalizedReportsListPageAccessControl}
        />
      </NavFolder>
      <NavFolder
        Icon={RatingIcon}
        title="campaigns.title"
        accessControl={retailerFundedMarketingAccessControl as AccessControlConfig}
        navEntryPoint={NavEntryPoint.RetailerFundedMarketing}
      />
      <NavFolder
        Icon={InventoryGoodIcon}
        title="inventoryIntelligence.title"
        accessControl={insightsPortalInventoryIntelligenceAccessControl as AccessControlConfig}
      >
        <NavItem
          route="insights-portal-inventory-intelligence"
          title="inventoryIntelligence.oosInsights.title"
          accessControl={insightsPortalInventoryIntelligenceAccessControl as AccessControlConfig}
        />
      </NavFolder>

      <NavFolder
        Icon={StoreIcon}
        title="storeOperations.title"
        accessControl={StoreOperationsRouteAccessControl}
        navEntryPoint={NavEntryPoint.StoreOperations}
      >
        <NavItem route="store-groups" title="settings.storeGroups.title" position={4} />
        {hasLaunchOnboarding && (
          <NavItem
            position={7}
            route="onboarding-store-management"
            title="onboarding.storeManagement"
            accessControl={onboardingAccessControl as AccessControlConfig}
          />
        )}
      </NavFolder>

      <NavFolder Icon={UserIcon} title="customers.title" navEntryPoint={NavEntryPoint.Customers} />

      <NavFolder
        Icon={AccountIcon}
        title="admin.title"
        accessControl={AdminAccessControl}
        navEntryPoint={NavEntryPoint.Admin}
      >
        <NavItem
          position={1}
          route="retailer-account-management-list"
          title="accountManagementDomain.nav.title"
          accessControl={AccountManagementRetailerAccessControl}
        />
        <NavItem
          position={4}
          route="app-admin"
          title="adminPanel.title"
          accessControl={AppAdminSectionAccessControl}
        />
      </NavFolder>
      {clientEnv.PUBLIC_CLIENT_ENVIRONMENT == 'production' && (
        <NavFolder
          Icon={FulfillmentTestingCenterIcon}
          title="app.devPortal.nav.header"
          accessControl={DevPortalFTCLinkAccessControl}
          navEntryPoint={NavEntryPoint.DevPortal}
        >
          <NavItem
            href={clientEnv.PUBLIC_CLIENT_RPP_DEV_URL}
            title="app.devPortal.nav.links.ftc"
            accessControl={AppDevPortalSectionAccessControl}
          />
        </NavFolder>
      )}
      <NavFolder
        Icon={CategoryIcon}
        title="merchandising.title"
        navEntryPoint={NavEntryPoint.Merchandising}
      >
        <NavItem
          route="merchandising-hero-banners"
          title="merchandising.hero-banners.title"
          accessControl={MerchandisingSectionAccessControl}
        />
      </NavFolder>
      <NavFolder
        Icon={ComputerIcon}
        title="storefront.nav.title"
        navEntryPoint={NavEntryPoint.Storefront}
      />
      <NavFolder Icon={EditIcon} title="settings.title" navEntryPoint={NavEntryPoint.Settings}>
        <NavItem
          route="store-groups"
          title="settings.storeGroups.title"
          accessControl={StoreGroupsSectionNavigationControl}
        />
        <NavItem
          route="stripeSettings"
          title="settings.stripe.title"
          accessControl={StripeAssociationsAccessControl}
        />
        {hasLaunchOnboarding && (
          <NavItem
            route="onboarding-retailer-settings"
            title="onboarding.retailerProfile"
            accessControl={onboardingAccessControl as AccessControlConfig}
          />
        )}
      </NavFolder>
      <NavFolder Icon={HelpIcon} title="help" navEntryPoint={NavEntryPoint.Help}>
        <NavItem title="help" onClick={ko.showKnowledgeOwl} />
        {(isTourPending || isTourCompleted) && (
          <NavItem
            title="welcomeTour"
            onClick={() => {
              startPickupWelcomeTour()
            }}
            id="nav-item-welcomeTour"
          />
        )}
        <NavItem title="press" href="https://instacart.com/press" />
        <NavItem title="privacy" href="https://instacart.com/privacy" />
        <NavItem title="security_and_trust" href="https://instacart.safebase.us/" />
      </NavFolder>
      <NavFolder
        Icon={TechnicalIcon}
        title="connect.title"
        navEntryPoint={NavEntryPoint.Connect}
        accessControl={ConnectSectionAccessControl}
      >
        <NavItem title="connect.configuration.title" route="connect-configuration" />
        <NavItem title="connect.callback-testing.title" route="connect-callback-testing" />
        <NavItem title="connect.fulfillmentSimulator.title" route="connect-fulfillment-simulator" />
      </NavFolder>
    </React.Fragment>
  )
}
